import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  Trash2,
  Plus,
  HelpCircle,
  Pencil,
  X,
  Save,
  Upload,
  Download,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link as LinkIcon } from "lucide-react";

interface Faq {
  id: string;
  question: string;
  answer_intro: string | null;
  answer_bullets: string[];
  category: string;
  sort_order: number;
  published: boolean;
}

const EMPTY_FORM = {
  question: "",
  answer_intro: "",
  bulletsText: "",
  category: "General",
  sort_order: 0,
  published: true,
};

function bulletsToText(bullets: string[]) {
  return bullets.join("\n");
}
function textToBullets(text: string) {
  return text
    .split("\n")
    .map((b) => b.trim())
    .filter(Boolean);
}

// ---------- Import parsing ----------
interface ImportRow {
  question: string;
  answer_intro: string | null;
  answer_bullets: string[];
  category: string;
  sort_order: number;
  published: boolean;
}

function coerceRow(raw: any, fallbackSort: number): ImportRow | null {
  if (!raw || typeof raw !== "object") return null;
  const question = String(raw.question ?? "").trim();
  if (!question) return null;

  // answer_bullets: accept array OR newline-separated string OR pipe-separated string
  let bullets: string[] = [];
  if (Array.isArray(raw.answer_bullets)) {
    bullets = raw.answer_bullets.map((b: unknown) => String(b).trim()).filter(Boolean);
  } else if (typeof raw.answer_bullets === "string") {
    bullets = raw.answer_bullets
      .split(/\r?\n|\|/)
      .map((b: string) => b.trim())
      .filter(Boolean);
  }

  return {
    question,
    answer_intro: raw.answer_intro ? String(raw.answer_intro).trim() : null,
    answer_bullets: bullets,
    category: String(raw.category ?? "General").trim() || "General",
    sort_order: Number.isFinite(Number(raw.sort_order)) ? Number(raw.sort_order) : fallbackSort,
    published: raw.published === undefined ? true : !!raw.published && raw.published !== "false",
  };
}

function parseCsv(text: string): any[] {
  // Minimal RFC-4180-ish CSV parser supporting quoted fields with commas/newlines.
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      cur.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      cur.push(field);
      rows.push(cur);
      cur = [];
      field = "";
    } else {
      field += c;
    }
  }
  if (field.length > 0 || cur.length > 0) {
    cur.push(field);
    rows.push(cur);
  }
  if (rows.length === 0) return [];
  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1)
    .filter((r) => r.some((cell) => cell.trim() !== ""))
    .map((r) => {
      const obj: any = {};
      headers.forEach((h, i) => {
        obj[h] = r[i] ?? "";
      });
      return obj;
    });
}

// ---------- Link inserter ----------
// Inserts a markdown-style [label](url) token at the textarea's cursor
// position. The FAQ page parses these and renders proper <a> / <Link> elements.
function LinkInserter({
  targetRef,
  value,
  onChange,
}: {
  targetRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement>;
  value: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");

  function openDialog() {
    const el = targetRef.current;
    const selected =
      el && "selectionStart" in el && el.selectionStart !== el.selectionEnd
        ? value.slice(el.selectionStart ?? 0, el.selectionEnd ?? 0)
        : "";
    setLabel(selected);
    setUrl("");
    setOpen(true);
  }

  function insert() {
    const trimmedLabel = label.trim();
    const trimmedUrl = url.trim();
    if (!trimmedLabel || !trimmedUrl) return;

    // Normalize URL: bare emails → mailto:, bare domains → https://
    let normalized = trimmedUrl;
    if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(normalized)) {
      normalized = `mailto:${normalized}`;
    } else if (
      !/^(https?:|mailto:|tel:|\/)/.test(normalized) &&
      /\./.test(normalized)
    ) {
      normalized = `https://${normalized}`;
    }

    const token = `[${trimmedLabel}](${normalized})`;
    const el = targetRef.current;
    const start = el && "selectionStart" in el ? el.selectionStart ?? value.length : value.length;
    const end = el && "selectionEnd" in el ? el.selectionEnd ?? value.length : value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    setOpen(false);
    // Restore focus + place cursor after the inserted token
    requestAnimationFrame(() => {
      const node = targetRef.current;
      if (node && "setSelectionRange" in node) {
        const pos = start + token.length;
        node.focus();
        (node as HTMLTextAreaElement).setSelectionRange(pos, pos);
      }
    });
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={openDialog}
        className="h-7 text-xs"
      >
        <LinkIcon className="h-3 w-3 mr-1" />
        Insert link
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert a link</DialogTitle>
            <DialogDescription>
              Wraps the text in <code className="px-1 py-0.5 rounded bg-muted text-xs">[label](url)</code>{" "}
              syntax. Use <code className="px-1 py-0.5 rounded bg-muted text-xs">/contact</code> for
              internal pages, full URLs for external sites, or an email address for a mailto link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="link-label">Visible text</Label>
              <Input
                id="link-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Contact page"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="link-url">URL, path, or email</Label>
              <Input
                id="link-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="/contact, https://give.abbashouse.org, hello@abbashouse.org"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    insert();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={insert} disabled={!label.trim() || !url.trim()}>
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function FAQManagement() {
  const { toast } = useToast();
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reorderingId, setReorderingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const introRef = useRef<HTMLTextAreaElement>(null);
  const bulletsRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fetchFaqs();
  }, []);

  async function fetchFaqs() {
    setLoading(true);
    const { data, error } = await supabase
      .from("faqs" as any)
      .select("*")
      .order("category", { ascending: true })
      .order("sort_order", { ascending: true });
    if (error) {
      toast({ title: "Error loading FAQs", description: error.message, variant: "destructive" });
    } else {
      setFaqs((data as unknown as Faq[]) ?? []);
    }
    setLoading(false);
  }

  function resetForm() {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
  }

  function startEdit(f: Faq) {
    setEditingId(f.id);
    setForm({
      question: f.question,
      answer_intro: f.answer_intro ?? "",
      bulletsText: bulletsToText(f.answer_bullets),
      category: f.category,
      sort_order: f.sort_order,
      published: f.published,
    });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.question.trim()) return;
    setSaving(true);

    const payload = {
      question: form.question.trim(),
      answer_intro: form.answer_intro.trim() || null,
      answer_bullets: textToBullets(form.bulletsText),
      category: form.category.trim() || "General",
      sort_order: Number(form.sort_order) || 0,
      published: form.published,
    };

    const op = editingId
      ? supabase.from("faqs" as any).update(payload).eq("id", editingId)
      : supabase.from("faqs" as any).insert(payload);

    const { error } = await op;
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: editingId ? "FAQ updated" : "FAQ added" });
      resetForm();
      fetchFaqs();
    }
    setSaving(false);
  }

  async function handleDelete(id: string) {
    setFaqs((prev) => prev.filter((f) => f.id !== id));
    const { error } = await supabase.from("faqs" as any).delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      fetchFaqs();
    } else {
      toast({ title: "FAQ deleted" });
    }
  }

  async function togglePublished(f: Faq) {
    // Optimistic
    setFaqs((prev) => prev.map((x) => (x.id === f.id ? { ...x, published: !f.published } : x)));
    const { error } = await supabase
      .from("faqs" as any)
      .update({ published: !f.published })
      .eq("id", f.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      fetchFaqs();
    } else {
      toast({ title: !f.published ? "Published" : "Unpublished" });
    }
  }

  // ---------- Reorder ----------
  // Move up/down within the same category by swapping sort_order with the neighbor.
  async function move(f: Faq, direction: "up" | "down") {
    const siblings = faqs
      .filter((x) => x.category === f.category)
      .sort((a, b) => a.sort_order - b.sort_order);
    const idx = siblings.findIndex((x) => x.id === f.id);
    const neighborIdx = direction === "up" ? idx - 1 : idx + 1;
    const neighbor = siblings[neighborIdx];
    if (!neighbor) return;

    setReorderingId(f.id);
    // Optimistic swap
    const newFaqs = faqs.map((x) => {
      if (x.id === f.id) return { ...x, sort_order: neighbor.sort_order };
      if (x.id === neighbor.id) return { ...x, sort_order: f.sort_order };
      return x;
    });
    setFaqs(newFaqs);

    // If both sort_orders happen to be equal, normalize them so the swap actually sticks.
    const aSort = f.sort_order === neighbor.sort_order ? neighbor.sort_order + (direction === "up" ? -1 : 1) : neighbor.sort_order;
    const bSort = f.sort_order === neighbor.sort_order ? f.sort_order : f.sort_order;

    const [r1, r2] = await Promise.all([
      supabase.from("faqs" as any).update({ sort_order: aSort }).eq("id", f.id),
      supabase.from("faqs" as any).update({ sort_order: bSort }).eq("id", neighbor.id),
    ]);

    if (r1.error || r2.error) {
      toast({
        title: "Reorder failed",
        description: r1.error?.message ?? r2.error?.message,
        variant: "destructive",
      });
      fetchFaqs();
    }
    setReorderingId(null);
  }

  // ---------- Import / Export ----------
  function handleExport() {
    const exportData = faqs.map((f) => ({
      question: f.question,
      answer_intro: f.answer_intro,
      answer_bullets: f.answer_bullets,
      category: f.category,
      sort_order: f.sort_order,
      published: f.published,
    }));
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `faqs-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function handleImportFile(file: File) {
    setImporting(true);
    try {
      const text = await file.text();
      const isCsv = file.name.toLowerCase().endsWith(".csv");
      let raw: any[] = [];

      if (isCsv) {
        raw = parseCsv(text);
      } else {
        const parsed = JSON.parse(text);
        raw = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.faqs) ? parsed.faqs : [];
      }

      if (raw.length === 0) {
        throw new Error("No rows found in file.");
      }

      const rows = raw
        .map((r, i) => coerceRow(r, (i + 1) * 10))
        .filter((r): r is ImportRow => r !== null);

      if (rows.length === 0) {
        throw new Error("No valid rows. Each row needs at minimum a 'question' field.");
      }

      // Upsert by `question` (the unique-ish key for content). We do this by
      // fetching existing rows with matching questions, then update vs insert
      // accordingly. Avoids needing a DB-side unique constraint.
      const questions = rows.map((r) => r.question);
      const { data: existing, error: lookupErr } = await supabase
        .from("faqs" as any)
        .select("id, question")
        .in("question", questions);

      if (lookupErr) throw lookupErr;

      const byQuestion = new Map<string, string>();
      ((existing as unknown as { id: string; question: string }[]) ?? []).forEach((e) =>
        byQuestion.set(e.question, e.id)
      );

      const updates = rows.filter((r) => byQuestion.has(r.question));
      const inserts = rows.filter((r) => !byQuestion.has(r.question));

      let updated = 0;
      let inserted = 0;
      const errors: string[] = [];

      // Updates run sequentially to avoid hammering the DB and to surface errors per-row.
      for (const r of updates) {
        const id = byQuestion.get(r.question)!;
        const { error } = await supabase
          .from("faqs" as any)
          .update({
            answer_intro: r.answer_intro,
            answer_bullets: r.answer_bullets,
            category: r.category,
            sort_order: r.sort_order,
            published: r.published,
          })
          .eq("id", id);
        if (error) errors.push(`Update "${r.question}": ${error.message}`);
        else updated++;
      }

      if (inserts.length > 0) {
        const { error } = await supabase.from("faqs" as any).insert(inserts);
        if (error) errors.push(`Insert ${inserts.length} new rows: ${error.message}`);
        else inserted = inserts.length;
      }

      toast({
        title: "Import complete",
        description: `${updated} updated, ${inserted} added${errors.length ? `, ${errors.length} errors` : ""}.`,
        variant: errors.length ? "destructive" : "default",
      });

      if (errors.length) console.error("[FAQ import] errors:", errors);

      fetchFaqs();
    } catch (err: any) {
      toast({
        title: "Import failed",
        description: err?.message ?? "Could not parse file.",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  // Group for display, ordered
  const grouped = faqs.reduce<Record<string, Faq[]>>((acc, f) => {
    (acc[f.category] ||= []).push(f);
    return acc;
  }, {});
  Object.values(grouped).forEach((arr) => arr.sort((a, b) => a.sort_order - b.sort_order));

  return (
    <div className="space-y-6">
      {/* Bulk import / export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Upload className="h-5 w-5" /> Bulk Import / Export
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upload a JSON or CSV file to refresh FAQs in bulk. Existing entries are matched by{" "}
            <code className="px-1 py-0.5 rounded bg-muted text-xs">question</code> and updated;
            new questions are inserted.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.csv,application/json,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImportFile(file);
              }}
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              <Upload className="h-4 w-4 mr-2" />
              {importing ? "Importing…" : "Import JSON / CSV"}
            </Button>
            <Button type="button" variant="outline" onClick={handleExport} disabled={faqs.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Export current FAQs
            </Button>
          </div>
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer font-medium">Expected file format</summary>
            <div className="mt-2 space-y-2">
              <p><strong>JSON:</strong> An array of objects, e.g.</p>
              <pre className="bg-muted rounded p-2 overflow-x-auto text-[11px] leading-relaxed">{`[
  {
    "question": "Who is eligible for the 90-Day Transition Program?",
    "answer_intro": "Optional intro paragraph.",
    "answer_bullets": ["Bullet one", "Bullet two"],
    "category": "Programs",
    "sort_order": 10,
    "published": true
  }
]`}</pre>
              <p>
                <strong>CSV:</strong> First row must be headers. Columns:{" "}
                <code>question</code>, <code>answer_intro</code>, <code>answer_bullets</code>,{" "}
                <code>category</code>, <code>sort_order</code>, <code>published</code>. In CSV,
                separate bullets with newlines or <code>|</code> inside a quoted{" "}
                <code>answer_bullets</code> cell.
              </p>
            </div>
          </details>
        </CardContent>
      </Card>

      {/* Add / Edit form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            {editingId ? <Pencil className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
            {editingId ? "Edit FAQ" : "Add a new FAQ"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Question (use literal phrasing, e.g. "Who is eligible for…?")</Label>
              <Input
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                placeholder="Who is eligible for the 90-Day Transition Program?"
                required
                maxLength={300}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Intro paragraph (optional)</Label>
                <LinkInserter
                  targetRef={introRef}
                  value={form.answer_intro}
                  onChange={(next) => setForm({ ...form, answer_intro: next })}
                />
              </div>
              <Textarea
                ref={introRef}
                rows={2}
                value={form.answer_intro}
                onChange={(e) => setForm({ ...form, answer_intro: e.target.value })}
                placeholder="Short context shown above the bullet list."
                maxLength={1000}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Answer bullets — one per line (these are what AI bots lift)</Label>
                <LinkInserter
                  targetRef={bulletsRef}
                  value={form.bulletsText}
                  onChange={(next) => setForm({ ...form, bulletsText: next })}
                />
              </div>
              <Textarea
                ref={bulletsRef}
                rows={6}
                value={form.bulletsText}
                onChange={(e) => setForm({ ...form, bulletsText: e.target.value })}
                placeholder={"Currently unhoused or at imminent risk of homelessness\nNo active violent criminal history\n…"}
              />
              <p className="text-xs text-muted-foreground">
                Tip: Place your cursor where you want a link, then click <strong>Insert link</strong>.
                You can also type the syntax by hand: <code className="px-1 py-0.5 rounded bg-muted">[Contact page](/contact)</code>.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Programs"
                  maxLength={80}
                />
              </div>
              <div className="space-y-2">
                <Label>Sort order</Label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={form.published}
                    onCheckedChange={(v) => setForm({ ...form, published: v })}
                  />
                  Published
                </label>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={saving}>
                <Save className="h-4 w-4 mr-1" />
                {saving ? "Saving…" : editingId ? "Save changes" : "Add FAQ"}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  <X className="h-4 w-4 mr-1" /> Cancel
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Existing FAQs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <HelpCircle className="h-5 w-5" /> FAQs ({faqs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-8">Loading…</p>
          ) : faqs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No FAQs yet.</p>
          ) : (
            <div className="space-y-6">
              {Object.entries(grouped).map(([category, items]) => (
                <div key={category}>
                  <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
                    {category}
                  </p>
                  <div className="space-y-2">
                    {items.map((f, idx) => (
                      <div
                        key={f.id}
                        className={`border border-border rounded-lg p-4 bg-background transition-opacity ${
                          f.published ? "" : "opacity-60"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          {/* Reorder controls */}
                          <div className="flex flex-col items-center gap-0.5 shrink-0 pt-0.5">
                            <button
                              onClick={() => move(f, "up")}
                              disabled={idx === 0 || reorderingId === f.id}
                              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              aria-label="Move up"
                              title="Move up within category"
                            >
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => move(f, "down")}
                              disabled={idx === items.length - 1 || reorderingId === f.id}
                              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              aria-label="Move down"
                              title="Move down within category"
                            >
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold">{f.question}</p>
                            {f.answer_intro && (
                              <p className="text-xs text-muted-foreground mt-1">{f.answer_intro}</p>
                            )}
                            {f.answer_bullets.length > 0 && (
                              <ul className="list-disc pl-5 mt-2 text-xs text-muted-foreground space-y-0.5">
                                {f.answer_bullets.slice(0, 3).map((b, i) => (
                                  <li key={i}>{b}</li>
                                ))}
                                {f.answer_bullets.length > 3 && (
                                  <li className="opacity-60">+{f.answer_bullets.length - 3} more…</li>
                                )}
                              </ul>
                            )}
                            <div className="flex flex-wrap items-center gap-3 mt-3">
                              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-muted text-muted-foreground">
                                sort {f.sort_order}
                              </span>
                              <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <Switch
                                  checked={f.published}
                                  onCheckedChange={() => togglePublished(f)}
                                  aria-label={f.published ? "Unpublish" : "Publish"}
                                />
                                <span className={f.published ? "text-primary font-medium" : "text-muted-foreground"}>
                                  {f.published ? "Published" : "Draft"}
                                </span>
                              </label>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => startEdit(f)}
                              className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                              aria-label="Edit FAQ"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button
                                  className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                  aria-label="Delete FAQ"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this FAQ?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    "{f.question}" will be permanently removed. This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(f.id)}>
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
